import { forwardRef, HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'

import { type LocalAudioTrack, type LocalVideoTrack } from 'livekit-client'

import { useMaybeRoomContext } from '@/src/context'
import { mergeProps } from '@/src/utils'

import { useIsMobile } from '@/src/hooks/useIsMobile'
import { useMediaDeviceSelect } from '@/src/hooks'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectMediaDevicesState, setMediaDevice } from '@/src/store/slices/mediaDevices'
import { selectBranding } from '@/src/store/slices/meetData'

/** @public */
export interface MediaDeviceSelectProps extends Omit<HTMLAttributes<HTMLUListElement>, 'onError'> {
  kind: MediaDeviceKind
  onActiveDeviceChange?: (deviceId: string) => void
  onDeviceListChange?: (devices: MediaDeviceInfo[]) => void
  onDeviceSelectError?: (e: Error) => void
  initialSelection?: string
  /** will force the browser to only return the specified device
   * will call `onDeviceSelectError` with the error in case this fails
   */
  exactMatch?: boolean
  track?: LocalAudioTrack | LocalVideoTrack
  /**
   * this will call getUserMedia if the permissions are not yet given to enumerate the devices with device labels.
   * in some browsers multiple calls to getUserMedia result in multiple permission prompts.
   * It's generally advised only flip this to true, once a (preview) track has been acquired successfully with the
   * appropriate permissions.
   *
   * @see {@link MediaDeviceMenu}
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices | MDN enumerateDevices}
   */
  requestPermissions?: boolean
  onError?: (e: Error) => void
}

/**
 * The `MediaDeviceSelect` list all media devices of one kind.
 * Clicking on one of the listed devices make it the active media device.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <MediaDeviceSelect kind='audioinput' />
 * </LiveKitRoom>
 * ```
 * @public
 */
export const MediaDeviceSelect = /* @__PURE__ */ forwardRef<
  HTMLUListElement,
  MediaDeviceSelectProps
>(function MediaDeviceSelect(
  {
    kind,
    initialSelection,
    onActiveDeviceChange,
    onDeviceListChange,
    onDeviceSelectError,
    exactMatch,
    track,
    requestPermissions,
    onError,
    ...props
  }: MediaDeviceSelectProps,
  ref,
) {
  const { isMobileByWidth: isMobile } = useIsMobile()

  const dispatch = useAppDispatch()
  const mediaDeviceStore = useAppSelector(selectMediaDevicesState)

  const { primary_color, text_primary_color } = useAppSelector(selectBranding)
  const room = useMaybeRoomContext()

  const { className, activeDeviceId, setActiveMediaDevice, devices } = useMediaDeviceSelect({
    kind,
    room,
    track,
    requestPermissions,
    onError,
  })

  const handleActiveDeviceChange = async (deviceId: string) => {
    try {
      if (kind === 'audiooutput') {
        localStorage.setItem('output-device-id', deviceId)
      } else {
        onActiveDeviceChange(deviceId)
      }

      await setActiveMediaDevice(deviceId, { exact: exactMatch ?? true })
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e))
      onDeviceSelectError?.(error)
    }
  }

  useEffect(() => {
    const setInitialSelection = async () => {
      if (initialSelection !== undefined) {
        try {
          await handleActiveDeviceChange(initialSelection)
        } catch (error) {
          console.error(`Error with active initial selection: ${error}`)
        }
      }
    }

    void setInitialSelection()
  }, [initialSelection])

  useEffect(() => {
    dispatch(
      setMediaDevice({
        [kind]: { showDevices: devices.length > 0 },
      }),
    )
  }, [devices, kind])

  const conditionOfHidePaddingBorderBottom = useMemo(
    () =>
      isMobile ||
      kind === 'audiooutput' ||
      (!room && kind === 'videoinput') ||
      !devices ||
      devices.length === 0 ||
      (kind === 'audioinput' && !mediaDeviceStore.audiooutput.showDevices),
    [room, devices, kind, isMobile, mediaDeviceStore.audiooutput.showDevices],
  )

  const mergedProps = useMemo(
    () =>
      mergeProps(
        props,
        { className: `${props?.className || ''} ${className} lk-list` },
        {
          style: {
            ...props?.style,
            borderBottom: conditionOfHidePaddingBorderBottom
              ? 'none'
              : '1px solid var(--lk-border-color)',
            paddingBottom: conditionOfHidePaddingBorderBottom ? 0 : 10,
            overflowY: 'auto',
            maxHeight: '300px',
          },
        },
      ),
    [className, conditionOfHidePaddingBorderBottom, props],
  )

  return (
    <ul ref={ref} {...mergedProps}>
      {(devices ?? []).map(({ deviceId, label }) => (
        <li
          key={deviceId}
          id={deviceId}
          data-lk-active={deviceId === activeDeviceId}
          aria-selected={deviceId === activeDeviceId}
          role="option"
        >
          <button
            className="lk-button"
            style={{
              color: text_primary_color,
              backgroundColor: deviceId === activeDeviceId ? primary_color : '',
            }}
            onClick={() => handleActiveDeviceChange(deviceId)}
          >
            {label}
          </button>
        </li>
      ))}
    </ul>
  )
})
